import { useSearchParams } from "@/lib/router-compat";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { BcbsilHeroSection } from "./bcbsil-saint/BcbsilHeroSection";
import { BcbsilEmpathyAndQuiz } from "./bcbsil-saint/BcbsilEmpathyAndQuiz";
import { BcbsilDetailSections } from "./bcbsil-saint/BcbsilDetailSections";
import { BcbsilTakeawaysAndSources } from "./bcbsil-saint/BcbsilTakeawaysAndSources";
import {
  BcbsilFaqSection,
  bcbsilFaqItems,
} from "./bcbsil-saint/BcbsilFaqSection";
import { BcbsilStickyCta } from "./bcbsil-saint/BcbsilStickyCta";
import { getEmployerNameFromParams } from "./bcbsil-saint/employerParam";

const IMG_BRAIN_HERO =
  "https://vibe.filesafe.space/1787327131284421342/attachments/6b0fe381-ac91-4dfb-ae4a-8b8d9481d2a1.jpg";
const IMG_MRI =
  "https://vibe.filesafe.space/1787327131284421342/attachments/b681631f-6d1d-429b-a7e0-2e6a14fbd291.jpg";
const IMG_CHAIR =
  "https://vibe.filesafe.space/1787327131284421342/attachments/3fff347a-a078-45b9-b12f-d69dbd703bb2.jpg";

export default function BcbsIllinoisSaint() {
  const [searchParams] = useSearchParams();
  const employer = getEmployerNameFromParams(searchParams);

  const pageTitle = employer
    ? `SAINT® TMS for ${employer} Employees | BCBSIL Benefits`
    : "SAINT® TMS for BCBS of Illinois Members | Neuro Wellness";

  const pageDescription = employer
    ? `Covered under ${employer}'s Blue Cross and Blue Shield of Illinois plan? SAINT®, an FDA-cleared 5-day depression treatment, may be available to you. Free benefits check.`
    : "Covered by Blue Cross and Blue Shield of Illinois? SAINT®, an FDA-cleared 5-day depression treatment, may be available to you. Free benefits check.";
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: bcbsilFaqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased selection:bg-accent selection:text-primary">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical="/bcbs-illinois-saint"
        schemaJson={[faqSchema]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20 pb-16 lg:pb-0">
        {/* 1. HERO with compact eligibility form (above the fold) */}
        <BcbsilHeroSection />

        {/* 2. PROOF BAR — credibility stats */}
        <section className="bg-primary text-primary-foreground py-10 px-4 sm:px-8 border-y border-primary-deep">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
              <div className="border-b md:border-b-0 md:border-r border-white/15 pb-6 md:pb-0 md:pr-6">
                <div className="text-3xl sm:text-4xl font-extrabold text-accent tracking-tight mb-2">
                  79%
                </div>
                <p className="text-sm sm:text-base text-white/90 leading-snug">
                  of participants reached remission in a controlled clinical
                  trial¹
                </p>
              </div>

              <div className="border-b md:border-b-0 md:border-r border-white/15 pb-6 md:pb-0 md:pr-6">
                <div className="text-3xl sm:text-4xl font-extrabold text-accent tracking-tight mb-2">
                  2.6 days
                </div>
                <p className="text-sm sm:text-base text-white/90 leading-snug">
                  on average to remission¹
                </p>
              </div>

              <div>
                <div className="text-3xl sm:text-4xl font-extrabold text-accent tracking-tight mb-2">
                  5 days
                </div>
                <p className="text-sm sm:text-base text-white/90 leading-snug">
                  of treatment, compared with 6 to 9 weeks for standard TMS
                </p>
              </div>
            </div>

            <p className="mt-8 text-xs text-white/60 text-center md:text-left leading-relaxed">
              ¹ Cole EJ, et al. American Journal of Psychiatry, 2022. Adults
              with treatment-resistant depression. Individual results vary.{" "}
              <a
                href="/compare-treatments"
                className="underline hover:text-accent text-white/80"
              >
                Compare treatments
              </a>
            </p>
          </div>
        </section>

        {/* 3. EMPATHY + QUIZ — "you've done everything" + eligibility quiz */}
        <BcbsilEmpathyAndQuiz />

        {/* 4. WHAT SAINT IS + COVERAGE + TESTIMONIALS + BOTTOM OPT-IN */}
        <BcbsilDetailSections
          mriImg={IMG_MRI}
          brainHeroImg={IMG_BRAIN_HERO}
          chairImg={IMG_CHAIR}
        />

        {/* 5. KEY TAKEAWAYS & MEDICAL REVIEW / SOURCES */}
        <BcbsilTakeawaysAndSources />

        {/* 6. FAQ */}
        <BcbsilFaqSection />
      </main>

      <Footer />

      {/* Sticky mobile CTA */}
      <BcbsilStickyCta />
    </div>
  );
}
